import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { SessionType } from 'models/Message';
import ResolveImage from 'components/ResolveImage';
import utils from 'utils';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import { useTranslation } from 'react-i18next';
import { getImgUrl } from 'components/ResolveImage/utils';
import MessagingContext from '../../../../providers/Messaging/Messaging.context';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import { SIZE } from 'models/Size';
import useMessagingNavigation from '../../../../hooks/useMessagingNavigation';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import useMessageRepresented from '../../../../hooks/useMessageRepresented';
import ArchiveIcon from 'icons/Archive.icon';
import useClosedChat from '../../../../hooks/useClosedChat';

import './SessionCard.styles.scss';

type SessionCardProps = {
  className?: string;
  session: SessionType;
  markSessionAsUnread: (sessionId: string) => void;
} & Omit<NavLinkProps, 'to' | 'children'>;

const SessionCard: React.FC<SessionCardProps> = (props) => {
  const { className, session, markSessionAsUnread, ...rest } = props;

  const { sessionId } = useContext(MessagingContext);

  const {
    _id,
    isDirectMessage,
    interlocutor,
    isGroupChat,
    latestMessageAt,
    hasUnreadMessages,
    isExternalCommunication,
  } = session;

  const isActive = useMemo(() => _id === sessionId, [_id, sessionId]);

  const { isArchived } = useClosedChat(session);

  const { interlocutorFullName, dealName, dealLogo, representerName } =
    useMessageRepresented()(session);

  const { t } = useTranslation();

  const navigationLink = useMessagingNavigation()(_id);

  return (
    <div
      className={classNames(
        'session-card-wrapper',
        { 'session-card-wrapper--active': isActive },
        { 'session-card-wrapper--archived': isArchived },
        { 'session-card-wrapper--new-message': hasUnreadMessages },
        className,
      )}
    >
      <NavLink
        {...rest}
        to={navigationLink}
        className={({ isActive }) =>
          classNames('session-card', {
            'session-card--active': isActive,
          })
        }
      >
        {isDirectMessage ? (
          <AvatarWithInitials user={interlocutor} size={SIZE.BIG} />
        ) : (
          <ResolveImage
            className="session-card__image"
            src={getImgUrl(dealLogo, '100')}
          />
        )}

        {isDirectMessage ? (
          <div className="session-card__content">
            <div className="session-card__content__title">
              <p>{interlocutorFullName}</p>
              <p>
                <span>{utils.shortPastTime(new Date(latestMessageAt))}</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="session-card__content">
            <div className="session-card__content__title">
              <p>{dealName}</p>
              <p
                className={classNames({
                  external: isExternalCommunication,
                })}
              >
                {isArchived && <ArchiveIcon className="archive" />}
                {isExternalCommunication && <span>{t('3rdParty')}</span>}
                {isGroupChat && <GroupUsersIcon className="group" />}
                <span>{utils.shortPastTime(new Date(latestMessageAt))}</span>
              </p>
            </div>
            <p className="session-card__content__representer">
              {representerName}
            </p>
          </div>
        )}
      </NavLink>

      {!isExternalCommunication && !hasUnreadMessages && (
        <div className="session-card-wrapper__actions">
          <ThreeDotsDropdown
            options={[
              {
                label: t('markUnread'),
                onClick: () => markSessionAsUnread(session._id),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default SessionCard;
